// src/components/TraderProfile/TraderActions.jsx
// Acciones sobre otro trader: seguir, bloquear, reportar + indicador de presencia
// (en línea / visto hace X). Se auto-oculta si no estás logueado o es tu propio perfil.
import React, { useState, useEffect, useCallback } from 'react';
import { FiUserPlus, FiUserCheck, FiSlash, FiFlag, FiX } from 'react-icons/fi';
import { useAuth } from '../../Context/AuthContext';
import './TraderActions.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Headers de auth (leen el token del localStorage) — a nivel módulo para que sean
// estables (no rompen las deps de los hooks).
const authH = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

function ago(dateStr) {
  if (!dateStr) return null;
  const s = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (s < 60) return 'recién';
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  return `hace ${Math.floor(s / 86400)} d`;
}

export default function TraderActions({ traderId, traderName }) {
  const { user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [blocked, setBlocked]     = useState(false);
  const [presence, setPresence]   = useState(null);
  const [busy, setBusy]           = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason]       = useState('');
  const [reported, setReported]   = useState(false);

  const load = useCallback(async () => {
    try {
      const [fRes, bRes, pRes] = await Promise.all([
        fetch(`${API_URL}/api/follows`, { headers: authH() }),
        fetch(`${API_URL}/api/blocks`,  { headers: authH() }),
        fetch(`${API_URL}/api/presence`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: [traderId] }) }),
      ]);
      const f = await fRes.json().catch(() => ({}));
      const b = await bRes.json().catch(() => ({}));
      const p = await pRes.json().catch(() => ({}));
      setFollowing((f.followedIds || []).includes(traderId));
      setBlocked((b.blockedIds || []).includes(traderId));
      setPresence(p[traderId] || null);
    } catch {}
  }, [traderId]);

  useEffect(() => { if (user && traderId && user.id !== traderId) load(); }, [user, traderId, load]);

  if (!user || !traderId || user.id === traderId) return null;

  const toggleFollow = async () => {
    setBusy(true);
    try {
      if (following) { await fetch(`${API_URL}/api/follow/${traderId}`, { method: 'DELETE', headers: authH() }); setFollowing(false); }
      else { await fetch(`${API_URL}/api/follow`, { method: 'POST', headers: authH(), body: JSON.stringify({ userId: traderId }) }); setFollowing(true); }
    } catch {} finally { setBusy(false); }
  };
  const toggleBlock = async () => {
    setBusy(true);
    try {
      if (blocked) { await fetch(`${API_URL}/api/block/${traderId}`, { method: 'DELETE', headers: authH() }); setBlocked(false); }
      else { await fetch(`${API_URL}/api/block`, { method: 'POST', headers: authH(), body: JSON.stringify({ userId: traderId }) }); setBlocked(true); setFollowing(false); }
    } catch {} finally { setBusy(false); }
  };
  const sendReport = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/report`, { method: 'POST', headers: authH(), body: JSON.stringify({ reportedId: traderId, reason: reason.trim() }) });
      setReported(true); setReportOpen(false); setReason('');
    } catch {} finally { setBusy(false); }
  };

  return (
    <div className="ta">
      {presence && (
        <span className={`ta-presence ${presence.online ? 'on' : 'off'}`}>
          <span className="ta-dot" />
          {presence.online ? 'En línea' : (ago(presence.lastSeenAt) ? `Visto ${ago(presence.lastSeenAt)}` : 'Desconectado')}
        </span>
      )}
      <div className="ta-btns">
        <button type="button" className={`ta-btn ${following ? 'active' : ''}`} onClick={toggleFollow} disabled={busy}>
          {following ? <><FiUserCheck /> Siguiendo</> : <><FiUserPlus /> Seguir</>}
        </button>
        <button type="button" className={`ta-btn danger ${blocked ? 'active' : ''}`} onClick={toggleBlock} disabled={busy} title={blocked ? 'Desbloquear' : 'Bloquear'}>
          <FiSlash /> {blocked ? 'Bloqueado' : 'Bloquear'}
        </button>
        <button type="button" className="ta-btn" onClick={() => setReportOpen(o => !o)} disabled={busy || reported} title="Reportar">
          <FiFlag /> {reported ? 'Reportado' : 'Reportar'}
        </button>
      </div>

      {reportOpen && (
        <div className="ta-report">
          <div className="ta-report-head">
            <span>Reportar a @{traderName}</span>
            <button className="ta-report-x" onClick={() => setReportOpen(false)}><FiX /></button>
          </div>
          <textarea
            className="ta-report-input"
            placeholder="Contanos qué pasó (estafa, comportamiento sospechoso, etc.)"
            value={reason} onChange={e => setReason(e.target.value)} maxLength={500} rows={3}
          />
          <button className="ta-report-send" onClick={sendReport} disabled={busy || !reason.trim()}>Enviar reporte</button>
        </div>
      )}
    </div>
  );
}
